import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ROLE, normaliseRole, getSubjectClaims } from '../roles';
import { Paperclip, CheckCircle2, CreditCard, FileText, BookOpen } from 'lucide-react';
import { SCOPE_STATUS, buildScopeLookups, sortScopes } from '../examScope';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

/**
 * Notice & Payment, student and guardian view.
 *
 * Split out of ParentNotice rather than living behind a role flag inside it, because the two
 * audiences share nothing: staff issue notices, chase outstanding replies and reconcile payments;
 * a family reads, signs and pays. Keeping them in one file meant every staff loader needed a
 * "not a student" guard, and each of those guards was a role race waiting to happen.
 *
 * Presentation is the Pending tab lifted verbatim, so nothing about how a family sees a notice
 * changes with this split.
 */
const money = (cents) => (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signatureDocId = (nid, sid) => `${nid}__${sid}`;

const SETTLED_LABEL = {
  kpay_online: 'K-Pay (online)',
  kpay_octopus: 'K-Pay (Octopus at school)',
  school_override: 'Recorded by school',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-CA')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const MyNotice = () => {
  const { activeSchoolId, isMobile, userProfile, user } = useOutletContext();
  const { t } = useT();

  const ownEmail = (user?.email || '').toLowerCase();
  const ownRole = normaliseRole(userProfile?.role);
  const isParent = ownRole === ROLE.PARENT;
  const isStudent = ownRole === ROLE.STUDENT;
  const ownName = userProfile?.name || userProfile?.initial || ownEmail;
  const children = useMemo(() => userProfile?.children || [], [userProfile]);

  // A student answers for themselves; a guardian for every child linked to them.
  const mySids = useMemo(
    () => (isParent ? children.map(c => c.sid).filter(Boolean) : [userProfile?.studentId].filter(Boolean)),
    [isParent, children, userProfile],
  );

  const [myNotices, setMyNotices] = useState([]);
  const [mySignatures, setMySignatures] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [signingKey, setSigningKey] = useState('');

  // Revision scopes live here rather than in a fifth family module: the tab bar is the family's
  // whole navigation and four is what fits on a phone, so a fifth tab costs more than a second view
  // inside the one screen that is already about what the school has published.
  const [view, setView] = useState('notices');
  const [scopes, setScopes] = useState([]);
  const [scopeSettings, setScopeSettings] = useState(null);
  const [scopeLevel, setScopeLevel] = useState('mine');

  // A guardian with more than one child picks between them rather than reading a merged list where
  // two children's notices sit side by side and the only thing separating them is a "For ..." line.
  // Built from the claim and intersected with the profile's children, like the other family
  // modules, so it can never offer a child whose rows the rules would refuse.
  const [claims, setClaims] = useState(null);
  const [selectedSid, setSelectedSid] = useState('');
  useEffect(() => { if (isParent) getSubjectClaims(auth.currentUser).then(setClaims); }, [isParent]);

  // Only the published ones. The query filters on status because the rule requires it to: a rule
  // validates, it does not filter, so an unscoped read here would be refused outright rather than
  // quietly trimmed. That is the intended behaviour -- it fails during development instead of
  // shipping a screen that shows families a draft.
  useEffect(() => {
    if (!activeSchoolId) return undefined;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'))
      .then(snap => setScopeSettings(snap.exists() ? snap.data() : {}))
      .catch(() => setScopeSettings({}));
    return onSnapshot(
      query(collection(db, 'schools', activeSchoolId, 'exam_scope'), where('status', '==', SCOPE_STATUS.APPROVED)),
      snap => {
        const list = [];
        snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
        setScopes(list.sort(sortScopes));
      },
      e => console.error('Error loading exam scopes', e),
    );
  }, [activeSchoolId]);

  const childOptions = useMemo(() => {
    if (!isParent || !claims) return [];
    const allowed = new Set(claims.childSids || []);
    return children
      .filter(c => c.sid && allowed.has(c.sid))
      .map(c => ({ sid: c.sid, label: `${c.currentClass || '-'} - ${c.name || c.sid} (${c.sid})` }));
  }, [isParent, claims, children]);

  useEffect(() => {
    if (childOptions.length && !childOptions.some(c => c.sid === selectedSid)) {
      setSelectedSid(childOptions[0].sid);
    }
  }, [childOptions, selectedSid]);

  // Which children this view covers: the selected one for a guardian, the student themselves
  // otherwise. Everything below derives from this rather than from mySids directly.
  const activeSids = useMemo(
    () => (isParent ? (selectedSid ? [selectedSid] : []) : mySids),
    [isParent, selectedSid, mySids],
  );

  const cardStyle = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  };

  // Every query below filters on the field its security rule tests. That is not a stylistic choice:
  // a list Firestore cannot prove is refused outright rather than trimmed, so the query shape and
  // the rule have to be changed together or the page returns nothing at all.
  // parent_notice_recipient, not parent_notice: one row per (notice, student), carrying the notice
  // content denormalised. The notice document itself lists every recipient's SID and address, so it
  // is staff-only -- a rule cannot hand back part of a document.
  //
  // A student matches studentEmail; a guardian matches guardianEmails, which returns every child's
  // rows in one query and is narrowed to the selected child below.
  useEffect(() => {
    if (!activeSchoolId || !ownEmail || !(isParent || isStudent)) return;
    const q = isParent
      ? query(
          collection(db, 'schools', activeSchoolId, 'parent_notice_recipient'),
          where('guardianEmails', 'array-contains', ownEmail),
        )
      : query(
          collection(db, 'schools', activeSchoolId, 'parent_notice_recipient'),
          where('studentEmail', '==', ownEmail),
        );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => String(b.issue_datetime || '').localeCompare(String(a.issue_datetime || '')));
      setMyNotices(list);
    }, (e) => console.error('Error loading my notices', e));
    return () => unsub();
  }, [activeSchoolId, ownEmail, isParent, isStudent]);

  useEffect(() => {
    if (!activeSchoolId || !ownEmail || !(isParent || isStudent)) return;
    const q = query(
      collection(db, 'schools', activeSchoolId, 'parent_notice_signature'),
      where(isParent ? 'guardianEmail' : 'studentEmail', '==', ownEmail),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      setMySignatures(list);
    }, (e) => console.error('Error loading my signatures', e));
    return () => unsub();
  }, [activeSchoolId, ownEmail, isParent, isStudent]);

  // The query above matches guardianEmail on the SLIP, which names whoever actually signed -- so a
  // second guardian, who never signs, would see every notice as unsigned. The rule does let them
  // read the slip (it checks guardianEmail2 on the child's profile), but only through a profile
  // get() that a list query cannot be proven against. Reading by document id sidesteps that
  // entirely: single-document reads are evaluated against the document itself.
  const [extraSignatures, setExtraSignatures] = useState([]);
  useEffect(() => {
    if (!isParent || !activeSchoolId || myNotices.length === 0 || activeSids.length === 0) {
      setExtraSignatures([]);
      return;
    }
    let cancelled = false;
    const pairs = myNotices
      .filter(r => activeSids.includes(r.sid))
      .map(r => [r.noticeId, r.sid]);

    Promise.all(pairs.map(([nid, sid]) =>
      getDoc(doc(db, 'schools', activeSchoolId, 'parent_notice_signature', signatureDocId(nid, sid)))
        .then(d => (d.exists() ? { uid: d.id, ...d.data() } : null))
        // A missing slip is the normal case (nobody has signed yet), and a refused one means this
        // guardian is not on that child -- neither should break the rest of the list.
        .catch(() => null),
    )).then(rows => {
      if (!cancelled) setExtraSignatures(rows.filter(Boolean));
    });

    return () => { cancelled = true; };
  }, [isParent, activeSchoolId, myNotices, activeSids]);

  // Merged by document id so a slip this guardian signed themselves is not counted twice.
  const allSignatures = useMemo(() => {
    const byId = new Map();
    [...mySignatures, ...extraSignatures].forEach(sig => byId.set(sig.uid, sig));
    return [...byId.values()];
  }, [mySignatures, extraSignatures]);

  useEffect(() => {
    if (!activeSchoolId || !ownEmail || !(isParent || isStudent)) return;
    const q = isParent
      ? query(
          collection(db, 'schools', activeSchoolId, 'payment_assignment'),
          where('guardianEmails', 'array-contains', ownEmail),
        )
      : query(
          collection(db, 'schools', activeSchoolId, 'payment_assignment'),
          where('studentEmail', '==', ownEmail),
        );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => String(b.created_datetime || '').localeCompare(String(a.created_datetime || '')));
      setPayments(list);
    }, (e) => console.error('Error loading payments', e));
    return () => unsub();
  }, [activeSchoolId, ownEmail, isParent, isStudent]);

  useEffect(() => {
    if (!activeSchoolId || !ownEmail || !(isParent || isStudent)) return;
    const q = query(
      collection(db, 'schools', activeSchoolId, 'payment_record'),
      where(isParent ? 'guardianEmail' : 'studentEmail', '==', ownEmail),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      setPaymentRecords(list);
    }, (e) => console.error('Error loading payment records', e));
    return () => unsub();
  }, [activeSchoolId, ownEmail, isParent, isStudent]);

  const mySignedKeys = useMemo(
    () => new Set(allSignatures.map(s => `${s.noticeId}__${s.sid}`)),
    [allSignatures],
  );

  // Each row IS a (notice, student) pair now, so this filters rather than cross-joins.
  const pendingRows = useMemo(() => myNotices
    .filter(r => (activeSids || []).includes(r.sid))
    .map(r => {
      const child = children.find(c => c.sid === r.sid);
      return {
        key: r.uid,
        notice: { ...r, uid: r.noticeId },
        sid: r.sid,
        childName: child?.name || '',
        signed: mySignedKeys.has(`${r.noticeId}__${r.sid}`),
        signature: allSignatures.find(x => x.noticeId === r.noticeId && x.sid === r.sid) || null,
      };
    }), [myNotices, activeSids, children, mySignedKeys, allSignatures]);

  const paymentRows = useMemo(() => payments
    .filter(r => (activeSids || []).includes(r.sid))
    .map(r => {
      const child = children.find(c => c.sid === r.sid);
      const rec = paymentRecords.find(x => x.payId === r.payId && x.sid === r.sid);
      return {
        key: r.uid,
        payment: { ...r, uid: r.payId },
        sid: r.sid,
        childName: child?.name || '',
        paid: rec?.status === 'paid',
        record: rec || null,
      };
    }), [payments, activeSids, children, paymentRecords]);

  // Only a guardian may sign, and only the PRIMARY one. The matching Firestore rule reads
  // primaryGuardianEmail from the child's own profile rather than trusting anything submitted here,
  // so a second guardian pressing this would be refused at the data layer regardless.
  const handleParentSign = async (row) => {
    if (!isParent) return;
    if (!window.confirm(`Sign "${row.notice.title}" for ${row.childName || row.sid}?`)) return;
    setSigningKey(row.key);
    try {
      const child = children.find(c => c.sid === row.sid);
      await setDoc(doc(db, 'schools', activeSchoolId, 'parent_notice_signature', signatureDocId(row.notice.uid, row.sid)), {
        noticeId: row.notice.uid,
        sid: row.sid,
        studentEmail: child?.email || '',
        studentName: child?.name || '',
        currentClass: child?.currentClass || '',
        classNumber: child?.classNumber || '',
        guardianEmail: ownEmail,
        guardianName: ownName,
        signedAt: new Date().toISOString(),
        signedByEmail: ownEmail,
      });
    } catch (err) {
      console.error('Error signing notice', err);
      alert(t('myNotice.signFailed', { message: err.message }));
    }
    setSigningKey('');
  };

  const scopeLook = useMemo(() => buildScopeLookups(scopeSettings), [scopeSettings]);

  // The level of whoever this view is currently about -- the student, or the selected child.
  const myLevel = useMemo(() => {
    const sid = activeSids[0];
    const child = children.find(c => c.sid === sid);
    return child ? scopeLook.levelForClass(child.currentClass) : '';
  }, [activeSids, children, scopeLook]);

  // Defaults to the viewer's own form. Every level is readable -- a scope carries no roster and is
  // about a syllabus, not a person -- but a first-former has no use for the S6 list, and "show all"
  // is there for the family with children in two forms who wants one page.
  const visibleScopes = useMemo(
    () => (scopeLevel === 'mine' && myLevel ? scopes.filter(s => s.classLevel === myLevel) : scopes),
    [scopes, scopeLevel, myLevel],
  );

  const scopeGroups = useMemo(() => {
    const map = new Map();
    visibleScopes.forEach(s => {
      const key = `${s.schoolYear}|${s.assessmentCode}`;
      if (!map.has(key)) map.set(key, { key, schoolYear: s.schoolYear, assessmentCode: s.assessmentCode, rows: [] });
      map.get(key).rows.push(s);
    });
    return [...map.values()];
  }, [visibleScopes]);

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* The screen used to open with a tabs bar whose only tab was a label with no onClick --
          the tab bar in the shell now names the screen, so all that is left here is the control
          that genuinely belongs to it. The row disappears entirely when there is nothing in it,
          which for a student is always. */}
      {/* Two real tabs with real handlers. The strip used to hold a single label with no onClick,
          which is the shape the platform has been removing -- it looked like navigation and was
          furniture. Now it is navigation, and the child selector rides on the same row. */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {[['notices', t('myNotice.tabs.toSign'), FileText], ['scopes', t('myNotice.tabs.examScope'), BookOpen]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setView(id)} className={`theme-tab ${view === id ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
              <div className="theme-tab-icon"><Icon size={18} /></div>
              {label}
            </button>
          ))}
        </div>
        {isParent && childOptions.length > 1 && (
          <select
            className="input-field"
            aria-label={t('family.child')}
            value={selectedSid}
            onChange={e => setSelectedSid(e.target.value)}
            style={{ margin: 0, width: 'auto', minWidth: '200px', maxWidth: '100%' }}
          >
            {childOptions.map(c => <option key={c.sid} value={c.sid}>{c.label}</option>)}
          </select>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {view === 'notices' && (
          <>

        
            {/* Hidden when there is nothing to head up -- otherwise someone with only a payment
                outstanding gets a "Notices" heading sitting above no notices at all. Kept in the
                empty state, where it gives the "Nothing awaiting attention" card its context. */}
            {(pendingRows.length > 0 || paymentRows.length === 0) && (
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>
                  {isParent ? t('myNotice.noticesToSign') : t('myNotice.noticesForGuardian')}
                </h2>
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
                  {isParent
                    ? t('myNotice.guardianIntro')
                    : t('myNotice.studentIntro')}
                </p>
              </div>
            )}

            {pendingRows.length === 0 && paymentRows.length === 0 ? (
              <div style={{ ...cardStyle, padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                {t('myNotice.nothingPending')}
              </div>
            ) : pendingRows.length === 0 ? null : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {pendingRows.map(row => {
                  const n = row.notice;
                  return (
                    <div key={row.key} style={{ ...cardStyle, padding: isMobile ? '0.85rem' : '1.15rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', borderLeft: `4px solid ${row.signed ? 'var(--color-success)' : 'var(--color-warning)'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                        <strong style={{ fontSize: '1rem' }}>{n.title}</strong>
                        <span style={{
                          fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap',
                          color: row.signed ? 'var(--color-success-text)' : 'var(--color-warning-text)',
                        }}>
                          {row.signed ? '● SIGNED' : '● AWAITING PARENT SIGNATURE'}
                        </span>
                      </div>

                      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                        {isParent && row.childName ? `For ${row.childName} · ` : ''}
                        {n.issuerId ? `From ${n.issuerId} · ` : ''}{formatWhen(n.issue_datetime)}
                      </div>

                      <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.content}</div>

                      {/* One notice can cover several days. Shown as a list rather than left in the
                          wording, so a parent signing once can see exactly what they are agreeing
                          to -- a slip that covers five days without naming them is signed blind. */}
                      {Array.isArray(n.eventDates) && n.eventDates.length > 0 && (
                        <div style={{ marginTop: '0.6rem', padding: '0.55rem 0.7rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
                            {n.eventDates.length === 1 ? 'Date' : `${n.eventDates.length} dates — signing once covers all of them`}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                            {[...n.eventDates].sort().map(d => (
                              <span key={d} style={{ fontSize: '0.8rem', padding: '0.15rem 0.45rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
                                {d}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {n.attachmentUrl && (
                        <a href={n.attachmentUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <Paperclip size={14} /> {n.attachmentName}
                        </a>
                      )}

                      {row.signed ? (
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-success-text)' }}>
                          Signed {formatWhen(row.signature?.signedAt)}
                          {row.signature?.guardianName ? ` by ${row.signature.guardianName}` : ''}
                        </div>
                      ) : isParent ? (
                        <button className="btn-primary" onClick={() => handleParentSign(row)} disabled={signingKey === row.key} style={{ alignSelf: 'flex-start', width: 'auto', padding: '0.55rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <CheckCircle2 size={15} /> {signingKey === row.key ? t('myNotice.signing') : t('myNotice.sign')}
                        </button>
                      ) : (
                        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                          {t('myNotice.signedByGuardian')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {paymentRows.length > 0 && (
              <>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: '0.5rem 0 0' }}>{t('myNotice.tabs.payments')}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {paymentRows.map(row => {
                    const pmt = row.payment;
                    return (
                      <div key={row.key} style={{ ...cardStyle, padding: isMobile ? '0.85rem' : '1.15rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', borderLeft: `4px solid ${row.paid ? 'var(--color-success)' : 'var(--color-warning)'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                          <strong style={{ fontSize: '1rem' }}>{pmt.title}</strong>
                          <span style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--color-primary)' }}>${money(pmt.amountCents)}</span>
                        </div>

                        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                          {isParent && row.childName ? `For ${row.childName}` : ''}
                          {pmt.deadline ? `${isParent && row.childName ? ' \u00b7 ' : ''}Due ${pmt.deadline}` : ''}
                        </div>

                        {pmt.description && (
                          <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{pmt.description}</div>
                        )}

                        {row.paid ? (
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-success-text)', fontWeight: '600' }}>
                            PAID {row.record?.paidAt ? `on ${formatWhen(row.record.paidAt)}` : ''}
                            {row.record?.settledVia
                              ? ` \u00b7 ${t(`myNotice.settledVia.${row.record.settledVia}`, {}, SETTLED_LABEL[row.record.settledVia] || row.record.settledVia)}`
                              : ''}
                          </div>
                        ) : isParent ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-start' }}>
                            <button
                              className="btn-primary"
                              onClick={() => alert(
                                `${t('myNotice.kpayNotConnected')}\n\n`
                                + `${t('myNotice.kpayHandsOff', { amount: money(pmt.amountCents) })} `
                                + t('myNotice.kpayConfirms')
                              )}
                              style={{ width: 'auto', padding: '0.55rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                            >
                              <CreditCard size={15} /> {t('myNotice.payOnline')}
                            </button>
                            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                              {t('myNotice.payOctopus')}
                            </span>
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                            {t('myNotice.paidByGuardian')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {view === 'scopes' && (
          <>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('myNotice.scopeTitle')}</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
                {t('myNotice.scopeIntro')}
              </p>
            </div>

            {/* Only offered when it would do something. With no level resolved there is nothing to
                narrow to, and a toggle between "mine" and "all" that returns the same list twice is
                a control that teaches people to ignore controls. */}
            {myLevel && (
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {[['mine', t('myNotice.levelOnly', { level: scopeLook.levelName(myLevel) })], ['all', t('myNotice.allLevels')]].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setScopeLevel(id)} style={{
                    padding: '0.3rem 0.8rem', borderRadius: 'var(--radius-md)', fontSize: '0.82rem',
                    fontWeight: scopeLevel === id ? '700' : '500', cursor: 'pointer', whiteSpace: 'nowrap',
                    backgroundColor: scopeLevel === id ? 'var(--color-nav-active)' : 'transparent',
                    border: `1px solid ${scopeLevel === id ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    color: scopeLevel === id ? 'var(--color-text-main)' : 'var(--color-text-subtle)',
                  }}>{label}</button>
                ))}
              </div>
            )}

            {scopeGroups.length === 0 ? (
              <div style={{ ...cardStyle, padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                {t('myNotice.scopeNothing')}
              </div>
            ) : scopeGroups.map(g => (
              <div key={g.key} style={{ ...cardStyle, overflow: 'hidden' }}>
                <div style={{ padding: isMobile ? '0.75rem 0.85rem' : '0.85rem 1.25rem', borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)' }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: '700' }}>{scopeLook.assessmentName(g.assessmentCode)}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{g.schoolYear}</div>
                </div>
                {g.rows.map(s => (
                  <div key={s.uid} style={{ padding: isMobile ? '0.75rem 0.85rem' : '0.85rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700' }}>
                      {scopeLook.subjectLabel(s.subjectCode)}
                      <span style={{ fontWeight: '500', color: 'var(--color-text-muted)' }}> · {scopeLook.levelName(s.classLevel)}</span>
                    </div>
                    <div style={{ fontSize: '0.9rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: '0.3rem' }}>
                      {s.description}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

      </div>
    </div>
  );
};

export default MyNotice;
